import React, { useState } from "react";
import styled from "styled-components";
import AccordionTitle from "./PlatFormAccordionTitle";
import UserInfoContent from "./UserInfoContent";
import AudioTrackTitle from "./PlatFormAudioTrackTitle";
import TrackInfo from "./Trackinfo";
import Countries from "./PlatformCountries";
import ThemeStore from "stories/ThemeStore";
import {
    Accordion,
    AccordionItem,
    AccordionItemHeading,
    AccordionItemButton,
    AccordionItemPanel,
} from 'react-accessible-accordion';
import TopVector from "assets/top-vector.svg";
import BottomVector from "assets/bottom-vector.svg";
import { PlatformReleaseInfoProps, Track } from "../types";
import { useNavigate, useParams } from "react-router";
import { ProgressSpinner } from "primereact/progressspinner";
import IntlStore from "stories/IntlStore";
import { useLanguages } from "components/NewRelease/hooks/useLanguages";
import { FormattedMessage } from "react-intl";
import ReleaseNoticeModal from "components/NewRelease/Platform/components/ReleaseNoticeModal";
import { useRelease } from "components/NewRelease/hooks/useRelease";

export const PlatformReleaseInfo: React.FC<PlatformReleaseInfoProps> = ({ releases, audio, loading }) => {

    const params = useParams();
    const id = params.id;
    const navigate = useNavigate();
    const [expandedItems, setExpandedItems] = useState<string[]>(['release-info']);
    const [step, setStep] = useState<number>(1);
    const theme = ThemeStore.activeTheme;
    const { languages } = useLanguages() as { languages: any[] };
    const { submitRelease } = useRelease(id);

    const tracks = releases?.tracks || [];
    const deliveryId = 1;
    const paid = !!(releases as any)?.is_paid;

    const handleChange = (uuids: string[]) => {
        setExpandedItems(uuids);
    };

    const handlePublish = async () => {
        if (!id) return;
        try {
            await submitRelease(Number(id));
            navigate("/releases");
        } catch (e) {
            console.error("[ReleaseDetails] submitRelease failed", e);
        }
    };

    return (
        <>
            <Accordion
                allowZeroExpanded
                allowMultipleExpanded
                preExpanded={expandedItems}
                onChange={handleChange}>
                <AccordionWrapper>
                    <AccordionItem
                        uuid={'release-info'}
                        key={'release-info'}>
                        <AccordionItemHeading>
                            <StyledAccordionItemButton
                                style={{ borderRadius: expandedItems.includes('release-info') ? '25px 25px 0 0' : '25px' }}>
                                <AccordionTitle
                                    name='release-info'
                                    title={IntlStore.intl.formatMessage({
                                        id: "ReleaseDetails.PlatformReleaseInfo.ReleaseInformation",
                                        defaultMessage: "Track information"
                                    })}
                                    expandedItems={expandedItems}
                                    link={`/releases/new-release/${id}?step=release-info`}
                                    headline={true}
                                />
                                {
                                    expandedItems.includes('release-info')
                                        ? <BottomVector color={theme.palette.text.breadcrumbs} />
                                        : <TopVector color={theme.palette.text.breadcrumbs} />
                                }
                            </StyledAccordionItemButton>
                        </AccordionItemHeading>
                        <StyledAccordionItemPanel
                            style={{ borderRadius: expandedItems.includes('release-info') ? '0 0 25px 25px' : '25px' }}>
                            <UserInfoContent
                                releases={releases}
                                link={`/releases/new-release/${id}?step=upload-image`}
                                step={step} setStep={setStep} />
                        </StyledAccordionItemPanel>
                    </AccordionItem>
                    <AccordionSection style={{ height: loading ? '125px' : "auto" }}>
                        {loading ? (
                            <Loading>
                                <ProgressSpinner />
                            </Loading>
                        ) : (
                          <>
                            {/* Track information section header (show only once) */}
                            {tracks.length > 0 && (
                              <TrackInfoHeaderRow style={{ padding: "0 18px 10px" }}>
                                <TrackInfoTitleAndEdit>
                                  <TrackInfoTitle>
                                    <FormattedMessage
                                      id="ReleaseDetails.TrackInfo.PublishRelease"
                                      defaultMessage="Track information"
                                    />
                                  </TrackInfoTitle>
                                  <TrackInfoEditButton
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/releases/new-release/${id}?step=track-upload`);
                                    }}
                                  >
                                    <FormattedMessage
                                      id="ReleaseDetails.TrackInfo.Edit"
                                      defaultMessage="Edit"
                                    />
                                  </TrackInfoEditButton>
                                </TrackInfoTitleAndEdit>
                              </TrackInfoHeaderRow>
                            )}

                            {tracks.map((track: Track, index: number,) => {
                            const uuid = `track-${track.id}`;
                            const isExpanded = expandedItems.includes(uuid);
                            const trackArtistsText =
                                Array.isArray(track.artists) && track.artists.length
                                    ? track.artists.map(a => a.name).filter(Boolean).join(", ")
                                    : "";
                            const langMatch = Array.isArray(languages)
                              ? languages.find((l: any) =>
                                  String(l?.id ?? "") === String(track.metadata_language) ||
                                  String(l?.code ?? "") === String(track.metadata_language) ||
                                  String(l?.language_id ?? "") === String(track.metadata_language)
                                )
                              : undefined;
                            const trackLanguageText =
                              (langMatch?.language ?? langMatch?.name) || (track.metadata_language ? String(track.metadata_language) : "");
                            return (
                                <>
                                    <AccordionItem uuid={uuid} key={track.id}>
                                        <AccordionItemHeading>
                                            <StyledAccordionItemButton
                                                style={{ borderRadius: isExpanded ? '25px 25px 0 0' : '25px', }}>
                                                <TrackButtonLeft>
                                                <AudioTrackTitle
                                                    index={index + 1}
                                                    title={track.title}
                                                    artists={trackArtistsText}
                                                    audio={audio}
                                                    id={track.id} />
                                                </TrackButtonLeft>
                                                {isExpanded ? (
                                                    <BottomVector color={theme.palette.text.breadcrumbs} />
                                                ) : (
                                                    <TopVector color={theme.palette.text.breadcrumbs} />
                                                )}
                                            </StyledAccordionItemButton>
                                        </AccordionItemHeading>
                                        <StyledAccordionItemPanel
                                            style={{ borderRadius: isExpanded ? '0 0 25px 25px' : '25px', }}>
                                            <TrackInfo
                                                title={track.title}
                                                version={track.version}
                                                language={trackLanguageText}
                                                releases={releases}
                                                audio={audio}
                                                id={track.id}
                                                isrc={track.isrc}
                                                artists={track?.artists}
                                                contributors={track?.contributors}
                                                genres={track?.genres}
                                                link={`/releases/new-release/${id}?step=track-upload`} />
                                        </StyledAccordionItemPanel>
                                    </AccordionItem>
                                    {index !== tracks.length - 1 && <LineBox><Line /></LineBox>}
                                </>
                            );
                          })}
                          </>
                        )}
                    </AccordionSection>
                    <AccordionItem uuid={'platforms'} key={'platforms'}>
                        <AccordionItemHeading>
                            <StyledAccordionItemButton
                                style={{ borderRadius: expandedItems.includes('platforms') ? '25px 25px 0 0' : '25px' }}>
                                <AccordionTitle
                                    name='platforms'
                                    expandedItems={expandedItems}
                                    title={IntlStore.intl.formatMessage({
                                        id: "ReleaseDetails.PlatformReleaseInfo.PlatformCountries",
                                        defaultMessage: "Platform and countries"
                                    })}
                                    link={`/releases/new-release/${id}?step=publish`} />
                                {
                                    expandedItems.includes('platforms')
                                        ? <BottomVector color={theme.palette.text.breadcrumbs} />
                                        : <TopVector color={theme.palette.text.breadcrumbs} />
                                }
                            </StyledAccordionItemButton>
                        </AccordionItemHeading>
                        <StyledAccordionItemPanel
                            style={{ borderRadius: expandedItems.includes('platforms') ? '0 0 25px 25px' : '25px' }}>
                            <Countries releases={releases} />
                        </StyledAccordionItemPanel>
                    </AccordionItem>
                </AccordionWrapper>
            </Accordion >
            <AccordionSection>
                <ReleaseNoticeModal
                    deliveryId={deliveryId}
                    onPublish={handlePublish}
                    step={paid ? 6 : 5}
                    setStep={setStep}
                    releaseDate={(releases?.release_date ?? releases?.created_at) || undefined}
                />
            </AccordionSection>
        </>
    );
};

const AccordionWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 18px;
`
const Loading = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`
const StyledAccordionItemButton = styled(AccordionItemButton)`
    display: flex;
    gap: 18px;
    align-items: center;
    background-color: #FFFFFF;
    color: #000000;
    font-size: 18px;
    padding: 20px;
    border: none;
    cursor: pointer;
    transition: background-color 0.3s ease;
`

const TrackButtonLeft = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
`;

const TrackInfoHeaderRow = styled.div`
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: 12px;
    min-width: 0;
`;

const TrackInfoTitleAndEdit = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    margin-top: 10px;

`;

const TrackInfoTitle = styled.div`
    font-family: SF Pro Display;
    font-weight: 600;
    font-size: 18px;
    line-height: 25px;
    letter-spacing: 0;
    min-width: 0;
`;

const TrackInfoEditButton = styled.button`
    width: 51px;
    height: 24px;
    border-radius: 100px;
    border: none;
    font-family: SF Pro Display;
    font-weight: 600;
    font-size: 13px;
    line-height: 18px;
    letter-spacing: 0;
    cursor: pointer;
    flex: 0 0 auto;
`;
const StyledAccordionItemPanel = styled(AccordionItemPanel)`
    padding: 0 20px 25px 20px;
    background-color: #FFFFFF;
    font-size: 16px;
    color: #333;
`;

const AccordionSection = styled.div`
    width: 100%;
    border-radius: 25px;
    background: #ffffff;
    position: relative;
    background-color:#fff;
`;

const Line = styled.div`
    width: calc(100% - 36px);
    height: 1px;
    background-color: #00000033;
`;

const LineBox = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
`;

